import { ChangeEvent } from "react";

import { SelectEvent, TimePickerEvent } from "@king-ict/eupisi-ui/components";

import { MultipleChangeInformation } from "./MultipleChangeInformation";

type InputChangeEvent = ChangeEvent<
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement
  | SelectEvent
  | TimePickerEvent
>;

export const useInput = () => {
  const getIndexSuffix = (index: number) => {
    return `[${index}]`;
  };

  const getIndexedName = (index: number, name: string) => {
    return `${name}${getIndexSuffix(index)}`;
  };

  const resolveEventData = (event: InputChangeEvent) => {
    const { name, value } = event.target;
    let resolvedValue;

    const inputEvent = event as ChangeEvent<HTMLInputElement>;
    const inputType = inputEvent && inputEvent.target.type;

    if (inputType === "checkbox") {
      resolvedValue = inputEvent.target.checked;
    } else if (inputType === "radio") {
      resolvedValue = parseInt(value);
    } else if (inputType === "number" && value !== "" && !isNaN(value)) {
      const isDecimal = value && value % 1 !== 0;
      resolvedValue = isDecimal ? parseFloat(value) : parseInt(value);
    } else if (inputType === "file") {
      resolvedValue =
        inputEvent.target.files && inputEvent.target.files[0]
          ? {
              fileName: inputEvent.target.files[0].name,
              file: inputEvent.target.files[0],
            }
          : undefined;
    } else {
      resolvedValue = event.target.value;
    }

    return {
      name: name,
      value: resolvedValue,
    };
  };

  const toArrayName = (name: string): string[] => {
    return name.replace(/ *\[[^\]]*]/, "").split(".");
  };

  const toArrayIndex = (name: string) => {
    const arrayFullName = name.split(".");
    let arrayIndex = Array<number | undefined>();
    arrayFullName.forEach((item) => {
      const indexString = item.match(/\[(.*?)\]/g);
      let index: number | undefined = undefined;
      if (indexString) {
        index = +indexString[0].replace(/\[/g, "").replace(/\]/g, "");
      }
      arrayIndex.push(index);
    });
    return arrayIndex;
  };

  const setNestedValue = (obj: any, path: string[], value: any): any => {
    if (path.length === 1) {
      obj[path as any] = value;
      return;
    }
    return setNestedValue(obj[path[0]], path.slice(1), value);
  };

  const handleChange =
    (setState: (previousState: any) => void) => (event: InputChangeEvent) => {
      const { name, value } = resolveEventData(event);

      if (name) {
        setState((previousState: any) => ({
          ...previousState,
          [name]: value,
        }));
      }
    };

  const handleNestedChange =
    (setState: (previousState: any) => void) => (event: InputChangeEvent) => {
      const { name, value } = resolveEventData(event);

      if (name) {
        setState((previousState: any) => {
          const propNames = toArrayName(name);
          const propIndexes = toArrayIndex(name);
          let arrayPath: string[] = [];
          propNames.forEach((name, i) => {
            if (propIndexes && propIndexes[i] != null) {
              arrayPath.push("" + propIndexes[i]);
            }
            arrayPath.push(name);
          });
          let newObj = { ...previousState };
          setNestedValue(newObj, arrayPath, value);
          return newObj;
        });
      }
    };

  const handleArrayChange =
    (index: number, setState: (previousState: any) => void) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
      >
    ) => {
      const { name, value } = resolveEventData(event);
      const resolvedName =
        name && name.includes(getIndexSuffix(index))
          ? name.replace(getIndexSuffix(index), "")
          : name;

      if (resolvedName) {
        setState((previousState: any) => {
          return previousState.map((prevItem: any, i: number) => {
            if (i === index) return { ...prevItem, [resolvedName]: value };
            return prevItem;
          });
        });
      }
    };

  const handleMultipleChange =
    (
      valueProp: string,
      collection: any[],
      changeInformations: MultipleChangeInformation[],
      setState: (previousState: any) => void
    ) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
      >
    ) => {
      const { value } = resolveEventData(event);
      const valueItem = collection.find(
        (c) => (c[valueProp] as string) === value
      );

      setState((previousState: any) => ({
        ...previousState,
        ...changeInformations.reduce(
          (previous, current) => ({
            ...previous,
            [current.stateProp]: valueItem[current.valueProp],
          }),
          {}
        ),
      }));
    };

  return {
    getIndexedName,
    handleChange,
    handleNestedChange,
    handleArrayChange,
    handleMultipleChange,
  };
};
